import { isNil } from "lopermut"
import { z } from "zod"
import { Errors } from "../errors"

const RangeSchema = z
  .object({ gte: z.number().optional(), lt: z.number().optional() })
  .refine(({ gte, lt }) => !isNil(gte) || !isNil(lt))

const LatestNpsRule = z.object({
  latestNps: z.union([RangeSchema, z.null()]),
})

const SentimentRule = z.object({
  sentiment: z.union([RangeSchema, z.null()]),
})

const LastPurchaseDaysAgoRule = z.object({
  lastPurchaseDaysAgo: RangeSchema,
})

const HasNoShopifyPurchasesRule = z.object({
  hasNoShopifyPurchases: z.literal(true),
})

const RfmGroupRule = z.object({
  rfmGroup: z.string(),
})

const RemainingCustomersRule = z.object({
  remainingCustomers: z.literal(true),
})

const BaseRule = z.union([
  LatestNpsRule,
  SentimentRule,
  LastPurchaseDaysAgoRule,
  HasNoShopifyPurchasesRule,
  RfmGroupRule,
  RemainingCustomersRule,
])

const FixedRuleSchema: z.ZodType<FixedRule> = z.lazy(() =>
  z.union([
    // Base rules (leaf nodes)
    BaseRule,
    // AND operation containing multiple rules
    z.object({ AND: z.array(FixedRuleSchema).min(1, Errors.ONE_SEGMENT_RULE_REQUIRED) }),
    // OR operation containing multiple rules
    z.object({ OR: z.array(FixedRuleSchema).min(1, Errors.ONE_SEGMENT_RULE_REQUIRED) }),
  ]),
)

export const FixedSegmentGroupRules = z.record(
  z.string(), // name
  z.object({
    order: z.number(),
    rules: FixedRuleSchema,
  }),
)

const GroupBySchema = z.union([
  z.literal("country"),
  z.literal("locale"),
  z.literal("language"),
  z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]*\.\([^)]+\)|[a-zA-Z_][a-zA-Z0-9_]*\.[\S ]+$/, {
    error:
      "Must be a valid field name or dot notation (e.g., 'metadata.field', 'settings.theme', 'metadata.Hello there (I am between parentheses)')",
  }),
])

export const DynamicSegmentGroupRules = z.object({
  groupBy: GroupBySchema,
  maxSegments: z.number().optional().default(9),
})

export type FixedRule = z.infer<typeof BaseRule> | { AND: FixedRule[] } | { OR: FixedRule[] }
export type DynamicRule = z.infer<typeof DynamicSegmentGroupRules>
